import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Send,
    Bot,
    User,
    AlertCircle,
    RotateCw,
    X,
    Lock,
    Sparkles,
    Database,
} from "lucide-react";
import styles from "./CompanyChatbot.module.css";
import {
    ChatWebSocketService,
    CONNECTION_STATES,
    generateRequestId,
} from "../../../Services/ChatWebSocketService";
import { DEFAULT_DEVELOPMENT_CIN } from "../../../Services/chatConfig";

const QUICK_SUGGESTIONS = [
    "What is the registered office address of this company?",
    "What is the authorized and paid-up capital?",
    "List active directors and their appointment dates.",
];

const CompanyChatbot = ({
    cin = DEFAULT_DEVELOPMENT_CIN,
    companyName = "",
    isFloating = false,
    onClose = () => {},
}) => {
    const activeCin = (cin || DEFAULT_DEVELOPMENT_CIN).trim();
    const navigate = useNavigate();
    const location = useLocation();

    // Authentication State
    const [token] = useState(() => sessionStorage.getItem("token") || "");

    // Chat Connection & Messages State
    const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
    const [connectionDetail, setConnectionDetail] = useState("");
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState("");

    const wsServiceRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    // Auto-scroll to bottom
    const scrollToBottom = useCallback(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // WebSocket Initialization & Management
    useEffect(() => {
        // Initialize WebSocket Service with current CIN and token
        const service = new ChatWebSocketService(activeCin, token);
        wsServiceRef.current = service;

        const unsubscribeStatus = service.onStatusChange((status, detail) => {
            setConnectionState(status);
            if (detail) {
                setConnectionDetail(detail);
            } else if (status === CONNECTION_STATES.CONNECTED) {
                setConnectionDetail("");
            }
        });

        const unsubscribeMessage = service.onMessage((data) => {
            if (!data) return;

            const responseId = data.id;

            setMessages((prevMessages) => {
                // Look for pending assistant message matching this request ID
                const index = prevMessages.findIndex(
                    (m) => m.id === responseId && m.sender === "assistant" && m.pending
                );

                const timeStr = new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                });

                if (index !== -1) {
                    const updated = [...prevMessages];
                    if (data.type === "answer") {
                        updated[index] = {
                            ...updated[index],
                            pending: false,
                            text: data.answer || "No text answer provided.",
                            sql: data.sql || null,
                            rowCount: data.row_count,
                            timestamp: timeStr,
                        };
                    } else if (data.type === "error") {
                        updated[index] = {
                            ...updated[index],
                            pending: false,
                            error: data.detail || "An unexpected error occurred.",
                            timestamp: timeStr,
                        };
                    }
                    return updated;
                }

                // If no matching pending slot was found, append a new assistant message
                if (data.type === "answer") {
                    return [
                        ...prevMessages,
                        {
                            id: responseId || generateRequestId(),
                            sender: "assistant",
                            pending: false,
                            text: data.answer || "No answer provided.",
                            sql: data.sql || null,
                            rowCount: data.row_count,
                            timestamp: timeStr,
                        },
                    ];
                } else if (data.type === "error") {
                    return [
                        ...prevMessages,
                        {
                            id: responseId || generateRequestId(),
                            sender: "assistant",
                            pending: false,
                            error: data.detail || "An unexpected error occurred.",
                            timestamp: timeStr,
                        },
                    ];
                }

                return prevMessages;
            });
        });

        // Connect only if token is present (or attempt connection)
        service.connect();

        return () => {
            unsubscribeStatus();
            unsubscribeMessage();
            service.destroy();
        };
    }, [activeCin, token]);

    // =========================================================================
    // Chat Actions
    // =========================================================================
    const handleSendMessage = (textToSend = null) => {
        const questionText = (textToSend !== null ? textToSend : inputText).trim();

        if (!questionText) return;
        if (questionText.length > 2000) return;

        if (connectionState !== CONNECTION_STATES.CONNECTED) {
            return;
        }

        const id = generateRequestId();
        const timeStr = new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });

        // Immediately show User question and Assistant "Thinking..."
        const userMsg = {
            id,
            sender: "user",
            text: questionText,
            timestamp: timeStr,
        };

        const assistantPendingMsg = {
            id,
            sender: "assistant",
            pending: true,
            text: "",
            timestamp: timeStr,
        };

        setMessages((prev) => [...prev, userMsg, assistantPendingMsg]);
        setInputText("");

        // Focus back to input
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }

        try {
            wsServiceRef.current?.sendMessage(questionText, id);
        } catch (err) {
            console.error("Error sending message:", err);
            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === id && msg.sender === "assistant"
                        ? {
                              ...msg,
                              pending: false,
                              error: err.message || "Failed to send message.",
                          }
                        : msg
                )
            );
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleTextareaChange = (e) => {
        setInputText(e.target.value);
        // Auto grow
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(
                textareaRef.current.scrollHeight,
                120
            )}px`;
        }
    };


    const handleManualReconnect = () => {
        wsServiceRef.current?.reconnect();
    };

    // =========================================================================
    // RENDER: Unauthenticated / Inline Login View
    // =========================================================================
    if (!token) {
        return (
            <div
                className={`${styles.chatbotContainer} ${
                    isFloating ? styles.floatingChatbot : ""
                }`}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.botIconCircle}>
                            <Bot size={22} />
                        </div>
                        <div>
                            <h3 className={styles.headerTitle}>AI Company Assistant</h3>
                            <div className={styles.cinBadge}>
                                <Database size={11} />
                                <span>{activeCin}</span>
                            </div>
                        </div>
                    </div>
                    <div className={styles.headerRight}>
                        {isFloating && (
                            <button
                                className={styles.closeHeaderBtn}
                                onClick={onClose}
                                title="Close">
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Auth Required Notice */}
                <div className={styles.loginViewContainer}>
                    <div className={styles.loginCard}>
                        <div className={styles.loginHeader}>
                            <div className={styles.loginIconCircle}>
                                <Lock size={26} />
                            </div>
                            <h4 className={styles.loginTitle}>Authorization Required</h4>
                            <p className={styles.loginSubtitle}>
                                Please log in to query verified financial and corporate
                                intelligence for <strong>{companyName || activeCin}</strong>.
                            </p>
                        </div>

                        <button
                            type="button"
                            className={styles.loginSubmitBtn}
                            onClick={() =>
                                navigate("/login", { state: { from: location.pathname } })
                            }>
                            Log In
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // =========================================================================
    // RENDER: Authenticated Chatbot Interface
    // =========================================================================
    const isConnected = connectionState === CONNECTION_STATES.CONNECTED;
    const isConnecting = connectionState === CONNECTION_STATES.CONNECTING;

    return (
        <div
            className={`${styles.chatbotContainer} ${
                isFloating ? styles.floatingChatbot : ""
            }`}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.botIconCircle}>
                        <Bot size={22} />
                    </div>
                    <div>
                        <h3 className={styles.headerTitle}>AI Company Assistant</h3>
                        <div className={styles.cinBadge}>
                            <Database size={11} />
                            <span>{activeCin}</span>
                        </div>
                    </div>
                </div>

                <div className={styles.headerRight}>
                    <div className={styles.statusPill}>
                        <span
                            className={`${styles.statusDot} ${
                                isConnected
                                    ? styles.dotConnected
                                    : isConnecting
                                    ? styles.dotConnecting
                                    : styles.dotDisconnected
                            }`}
                        />
                        <span>
                            {isConnected
                                ? "Connected"
                                : isConnecting
                                ? "Connecting..."
                                : "Disconnected"}
                        </span>
                    </div>

                    {!isConnected && (
                        <button
                            className={styles.reconnectBtn}
                            onClick={handleManualReconnect}
                            title="Retry connection">
                            <RotateCw size={12} />
                            <span>Reconnect</span>
                        </button>
                    )}

                    {isFloating && (
                        <button
                            className={styles.closeHeaderBtn}
                            onClick={onClose}
                            title="Close Chatbot">
                            <X size={18} />
                        </button>
                    )}
                </div>
            </div>

            {/* Connection Detail Notice (if disconnected or error) */}
            {!isConnected && connectionDetail && (
                <div className={styles.noticeBar}>
                    <span>
                        <AlertCircle size={14} />
                        {connectionDetail}
                    </span>
                    <button
                        className={styles.reconnectBtn}
                        style={{ color: "#92400e", background: "#fef3c7" }}
                        onClick={handleManualReconnect}>
                        Retry
                    </button>
                </div>
            )}

            {/* Messages Body */}
            <div className={styles.messagesBody}>
                {messages.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}>
                            <Sparkles size={28} />
                        </div>
                        <h4 className={styles.emptyStateTitle}>
                            Ask Anything About {companyName || "This Company"}
                        </h4>
                        <p className={styles.emptyStateDesc}>
                            Connected to live PostgreSQL database. Ask natural language
                            questions to inspect registration, capital, directors, and state metrics.
                        </p>

                        <div className={styles.suggestionGrid}>
                            {QUICK_SUGGESTIONS.map((sug, idx) => (
                                <button
                                    key={idx}
                                    className={styles.suggestionChip}
                                    onClick={() => handleSendMessage(sug)}
                                    disabled={!isConnected}>
                                    <Sparkles size={14} style={{ color: "var(--AccentColor, #1F66FF)" }} />
                                    <span>{sug}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    messages.map((msg, index) => {
                        const isUser = msg.sender === "user";

                        return (
                            <div
                                key={`${msg.id}-${index}`}
                                className={`${styles.messageRow} ${
                                    isUser
                                        ? styles.userMessageRow
                                        : styles.assistantMessageRow
                                }`}>
                                {!isUser && (
                                    <div className={`${styles.avatar} ${styles.botAvatar}`}>
                                        <Bot size={18} />
                                    </div>
                                )}

                                {isUser ? (
                                    <div className={styles.userBubble}>
                                        <p style={{ margin: 0 }}>{msg.text}</p>
                                        <span className={styles.messageTime}>{msg.timestamp}</span>
                                    </div>
                                ) : (
                                    <div className={styles.assistantBubble}>
                                        {msg.pending ? (
                                            <div className={styles.thinkingContainer}>
                                                <span>Thinking</span>
                                                <div className={styles.typingDots}>
                                                    <span className={styles.typingDot} />
                                                    <span className={styles.typingDot} />
                                                    <span className={styles.typingDot} />
                                                </div>
                                            </div>
                                        ) : msg.error ? (
                                            <div className={styles.messageError}>
                                                <AlertCircle size={16} style={{ flexShrink: 0 }} />
                                                <span>{msg.error}</span>
                                            </div>
                                        ) : (
                                            <>
                                                <p className={styles.answerText}>{msg.text}</p>

                                                <div className={styles.metaFooter}>
                                                    <span className={styles.messageTime}>
                                                        {msg.timestamp}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {isUser && (
                                    <div
                                        className={styles.avatar}
                                        style={{
                                            background: "#e2e8f0",
                                            color: "#334155",
                                        }}>
                                        <User size={18} />
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className={styles.inputContainer}>
                <div className={styles.inputRow}>
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        className={styles.textarea}
                        placeholder={
                            isConnected
                                ? "Ask a question about this company..."
                                : "Connecting to chat assistant..."
                        }
                        value={inputText}
                        onChange={handleTextareaChange}
                        onKeyDown={handleKeyDown}
                        maxLength={2000}
                        disabled={!isConnected}
                    />

                    <button
                        type="button"
                        className={styles.sendBtn}
                        onClick={() => handleSendMessage()}
                        disabled={!isConnected || !inputText.trim()}
                        title="Send Message">
                        <Send size={18} />
                    </button>
                </div>

                <div className={styles.inputFooter}>
                    <span>Press Enter to send, Shift + Enter for new line</span>
                    <span
                        className={`${styles.charCount} ${
                            inputText.length >= 1950 ? styles.charCountOver : ""
                        }`}>
                        {inputText.length} / 2000
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CompanyChatbot;
